import type { ReactNode } from 'react';
import React, { useCallback, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { getLoader } from 'Components/utils/icon/icons.loaders';
export type IconName = (string | null | undefined) | null;
export type Props = {
  alt?: string | ReactNode;
  className?: string;
  dataTestid?: string;
  hasRtl?: boolean;
  isNotMonoChrome?: boolean;
  isNotSquare?: boolean;
  name?: IconName;
  rotation?: number;
  style?: import('react').CSSProperties;
  width?: string;
};

const loadIcon = (name: IconName) => {
  if (!name) {
    return null;
  }

  return getLoader(name);
};

export const Icon = ({
  alt,
  className,
  dataTestid,
  hasRtl = false,
  isNotMonoChrome = false,
  isNotSquare = false,
  name,
  rotation = 0,
  style,
  width = '1rem',
}: Props) => {
  const IconComponent = loadIcon(name);
  const [ratio, setRatio] = useState(100);
  // We need to keep placeholder because IE 11 does not correctly scale responsive svg
  const svgRef = useCallback((node) => {
    if (node !== null && isNotSquare) {
      try {
        const { width: w = 1, height: h = 0 } = node.getBBox();
        setRatio((h / w) * 100);
      } catch (e) {
        // getBBox throw an NS_ERROR_FAILURE exception on Firefox when icon is hidden
      }
    }
  }, []);

  if (!name) {
    return null;
  }

  const styles = {
    ...style,
    height: isNotSquare ? 'auto' : width,
    ...(rotation
      ? {
          transform: `rotate(${rotation}deg)`,
        }
      : null),
    width,
  };
  return (
    <span
      className={classnames(
        'inline-block overflow-hidden leading-0 text-inherit transition-all duration-250 ease-in',
        {
          'icon-monochrome': !isNotMonoChrome,
        },
        className,
      )}
      data-name={name}
      data-testid={dataTestid || 'icon'}
      style={styles}
    >
      <span
        className="relative block w-full"
        style={{
          paddingBottom: `${ratio}%`,
        }}
      >
        {IconComponent !== null && (
          <IconComponent
            aria-hidden="true"
            className={classnames('absolute inset-0 h-full w-full fill-current', {
              'transform ltr:scale-x-1 rtl:-scale-x-1': hasRtl,
            })}
            focusable="false"
            xRef={svgRef}
          />
        )}
        {alt && <span className="sr-only">{alt}</span>}
      </span>
    </span>
  );
};
